import React, { useState } from "react";
import { Modal, Spin, Typography, message as antdMessage } from "antd";
import { MdDownload, MdInsertDriveFile } from "react-icons/md";

import useAuthedMedia, { downloadAuthed } from "./useAuthedMedia";

const { Text } = Typography;

/**
 * One attachment, on either channel.
 *
 * Everything goes through `useAuthedMedia` rather than a bare `src`: these
 * files sit behind token auth on the API origin, so a browser-issued request
 * from the React origin gets HTML or a 401.
 */

const humanSize = (bytes) => {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
};

const isImage = (attachment) =>
  (attachment.content_type || "").startsWith("image/");
const isVideo = (attachment) =>
  (attachment.content_type || "").startsWith("video/");
const isAudio = (attachment) =>
  (attachment.content_type || "").startsWith("audio/");

const ImageAttachment = ({ attachment }) => {
  const { url, loading, error } = useAuthedMedia(attachment.url);
  const [preview, setPreview] = useState(false);

  if (loading) {
    return (
      <div style={{ width: 220, height: 140, display: "grid", placeItems: "center" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (error || !url) {
    return (
      <div
        style={{
          width: 220,
          padding: 12,
          background: "#f0f0f0",
          borderRadius: 6,
          textAlign: "center",
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {/* WhatsApp media is deleted by Meta after about 30 days, so an old
              thread legitimately has images that can no longer be fetched. */}
          Image unavailable
        </Text>
      </div>
    );
  }

  return (
    <>
      <img
        src={url}
        alt={attachment.filename || "attachment"}
        onClick={() => setPreview(true)}
        style={{
          maxWidth: 260,
          maxHeight: 280,
          borderRadius: 6,
          cursor: "pointer",
          display: "block",
        }}
      />
      <Modal
        open={preview}
        onCancel={() => setPreview(false)}
        footer={null}
        width="auto"
        centered
        styles={{ body: { padding: 0, textAlign: "center" } }}
      >
        <img
          src={url}
          alt={attachment.filename || "attachment"}
          style={{ maxWidth: "80vw", maxHeight: "80vh" }}
        />
      </Modal>
    </>
  );
};

const PlayableAttachment = ({ attachment, kind }) => {
  const { url, loading } = useAuthedMedia(attachment.url);
  if (loading) return <Spin size="small" />;
  if (!url) return <Text type="secondary">Media unavailable</Text>;
  return kind === "video" ? (
    <video controls style={{ maxWidth: 280, borderRadius: 6, display: "block" }}>
      <source src={url} type={attachment.content_type} />
    </video>
  ) : (
    <audio controls style={{ width: 260 }}>
      <source src={url} type={attachment.content_type} />
    </audio>
  );
};

const FileAttachment = ({ attachment }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadAuthed(attachment.url, attachment.filename);
    } catch (error) {
      antdMessage.error("Could not download that file");
    }
    setBusy(false);
  };

  return (
    <div
      onClick={busy ? undefined : download}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        background: "rgba(0,0,0,0.04)",
        borderRadius: 6,
        cursor: busy ? "wait" : "pointer",
        minWidth: 200,
      }}
    >
      <MdInsertDriveFile style={{ fontSize: 22, color: "#54656f", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {attachment.filename}
        </div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {humanSize(attachment.size)}
        </Text>
      </div>
      {busy ? <Spin size="small" /> : <MdDownload style={{ color: "#54656f" }} />}
    </div>
  );
};

const Attachment = ({ attachment }) => {
  if (!attachment?.url) return null;
  if (isImage(attachment)) return <ImageAttachment attachment={attachment} />;
  if (isVideo(attachment))
    return <PlayableAttachment attachment={attachment} kind="video" />;
  if (isAudio(attachment))
    return <PlayableAttachment attachment={attachment} kind="audio" />;
  return <FileAttachment attachment={attachment} />;
};

export default Attachment;
